import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import PageHero from "@/components/base/PageHero";
import EpisodeCard from "@/components/base/EpisodeCard";
import FavoriteButton from "@/components/base/FavoriteButton";
import { characters } from "@/mocks/characters";
import { episodes } from "@/mocks/episodes";
import { useSeo } from "@/hooks/useSeo";

type TabKey = "analysis" | "relations" | "episodes";

const tabs: { key: TabKey; label: string; icon: string }[] = [
  { key: "analysis", label: "角色解读", icon: "ri-file-text-line" },
  { key: "relations", label: "人物关系", icon: "ri-links-line" },
  { key: "episodes", label: "相关剧集", icon: "ri-play-list-2-line" },
];

export default function CharacterDetailPage() {
  const { id } = useParams<{ id: string }>();
  const character = characters.find((c) => c.id === id);
  const [tab, setTab] = useState<TabKey>("analysis");

  useSeo({
    title: character
      ? `${character.name} - 《年轻的母亲2》人物解析`
      : "角色未找到 - 年轻的母亲2",
    description: character
      ? `《年轻的母亲2》角色${character.name}（由${character.actor}饰演）人物解析：${character.intro}`
      : "未找到对应的角色内容，请返回角色分析页查看全部角色。",
    keywords: character
      ? `${character.name},年轻的母亲2角色,${character.actor},人物解析`
      : "年轻的母亲2,角色分析",
  });

  if (!character) {
    return (
      <div className="mx-auto w-full max-w-3xl px-4 py-24 text-center md:px-6">
        <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
          <i className="ri-user-unfollow-line text-3xl"></i>
        </span>
        <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">没有找到这个角色</h1>
        <p className="mt-3 text-sm text-foreground-600">该角色可能已调整或链接有误，返回角色分析页继续浏览。</p>
        <Link
          to="/characters"
          className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 font-label text-sm font-semibold text-background-50"
        >
          返回角色分析
        </Link>
      </div>
    );
  }

  const relatedEpisodes = episodes
    .filter((ep) => ep.cast.includes(character.actor) || ep.cast.includes(character.name))
    .slice(0, 3);

  return (
    <div className="w-full">
      <PageHero
        eyebrow={character.roleType === "lead" ? "主要角色" : "配角"}
        title={character.name}
        desc={character.tagline}
        breadcrumb={[
          { label: "首页", to: "/" },
          { label: "角色分析", to: "/characters" },
          { label: character.name },
        ]}
      />

      <div className="mx-auto w-full px-4 py-8 md:px-6 md:py-10">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[320px_1fr]">
          {/* 左：大头像与扮演者 */}
          <aside className="lg:sticky lg:top-24 lg:self-start">
            <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
              <div className="h-[380px] w-full overflow-hidden bg-secondary-200">
                <img
                  src={character.avatar}
                  alt={`《年轻的母亲2》角色 ${character.name}（由${character.actor}饰演）`}
                  title={`${character.name} - 年轻的母亲2 人物解析`}
                  className="h-full w-full object-top"
                />
              </div>
              <div className="p-5">
                <h2 className="font-heading text-xl font-extrabold text-foreground-950">
                  {character.name}
                </h2>
                <p className="mt-1 flex items-center gap-2 font-label text-sm text-foreground-600">
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-user-star-line text-base"></i>
                  </span>
                  由 {character.actor} 饰演
                </p>
                <div className="mt-4">
                  <FavoriteButton id={character.id} label="收藏该角色" />
                </div>
              </div>
            </div>
          </aside>

          {/* 右：可切换的信息块 */}
          <div>
            <div className="flex items-center gap-1 rounded-full border border-background-200 bg-background-50 p-1">
              {tabs.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setTab(t.key)}
                  className={`flex flex-1 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-full px-3 py-2 font-label text-sm transition-colors ${
                    tab === t.key
                      ? "bg-primary-500 font-semibold text-background-50"
                      : "text-foreground-600 hover:bg-background-100"
                  }`}
                >
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className={`${t.icon} text-base`}></i>
                  </span>
                  {t.label}
                </button>
              ))}
            </div>

            <div className="mt-6">
              {tab === "analysis" && (
                <div className="animate-fade-up">
                  <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
                    <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                      <span className="h-5 w-1 rounded-full bg-accent-500"></span>
                      角色简介
                    </h2>
                    <p className="mt-3 text-sm leading-8 text-foreground-700 md:text-base">
                      {character.intro}
                    </p>
                  </div>
                  <div className="mt-4 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
                    <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                      <span className="h-5 w-1 rounded-full bg-primary-500"></span>
                      背景与剧情解读
                    </h2>
                    <p className="mt-3 text-sm leading-8 text-foreground-700 md:text-base">
                      {character.background}
                    </p>
                  </div>
                </div>
              )}

              {tab === "relations" && (
                <div className="animate-fade-up rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
                  <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                    <span className="h-5 w-1 rounded-full bg-accent-500"></span>
                    人物关系图
                  </h2>
                  <p className="mt-2 text-sm text-foreground-600">
                    以 {character.name} 为中心的剧中关系网络。
                  </p>
                  <div className="mt-5 flex items-center gap-4">
                    <div className="flex flex-col items-center">
                      <div className="h-20 w-20 overflow-hidden rounded-full border-2 border-accent-400 bg-secondary-200">
                        <img
                          src={character.avatar}
                          alt={character.name}
                          className="h-full w-full object-top"
                        />
                      </div>
                      <span className="mt-2 font-label text-sm font-semibold text-foreground-950">
                        {character.name}
                      </span>
                    </div>
                    <div className="h-px flex-1 bg-background-300"></div>
                    <span className="flex h-6 w-6 items-center justify-center text-foreground-400">
                      <i className="ri-arrow-right-line text-lg"></i>
                    </span>
                  </div>
                  <ul className="mt-5 flex flex-col gap-3">
                    {character.relations.map((rel) => (
                      <li
                        key={rel.name}
                        className="flex items-start gap-3 rounded-md border border-background-200 bg-background-100 p-4"
                      >
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-secondary-100 text-secondary-800">
                          <i className="ri-user-3-line text-base"></i>
                        </span>
                        <div>
                          <p className="font-heading text-sm font-bold text-foreground-950">
                            {rel.name}
                          </p>
                          <p className="mt-0.5 text-sm text-foreground-600">{rel.desc}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {tab === "episodes" && (
                <div className="animate-fade-up">
                  {relatedEpisodes.length > 0 ? (
                    <>
                      <p className="mb-4 text-sm text-foreground-600">
                        {character.name} 出场较多的剧集，点击可查看对应剧情。
                      </p>
                      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                        {relatedEpisodes.map((ep) => (
                          <EpisodeCard key={ep.id} {...ep} />
                        ))}
                      </div>
                    </>
                  ) : (
                    <div className="rounded-lg border border-dashed border-background-300 bg-background-50 p-12 text-center text-sm text-foreground-600">
                      暂无关联剧集推荐。
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}